import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircleNotch, Cube, Clock, CaretDown, CaretUp } from "@phosphor-icons/react";
import { API, http } from "@/lib/constants";
import { cn, formatLovelace, formatRelativeTime, truncMid, unixToDate } from "@/lib/format";
import { RowShareBtn, CopyBtn } from "@/components/primitives";

// ---------- latest blocks widget (home page) ----------
const useRecentBlocks = (intervalMs = 20000, count = 6) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const retryRef = useRef(null);
  const hasData = useRef(false);
  const fetchRows = useCallback(async () => {
    try {
      const res = await http.get(`${API}/blocks/recent`, { params: { count }, timeout: 15000 });
      if (!Array.isArray(res.data)) throw new Error("Unexpected blocks payload");
      setRows(res.data);
      if (res.data.length) hasData.current = true;
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || { message: e.message });
      // Fast self-heal while we still have no blocks to show.
      if (!hasData.current) {
        clearTimeout(retryRef.current);
        retryRef.current = setTimeout(fetchRows, 6000);
      }
    } finally {
      setLoading(false);
    }
  }, [count]);
  useEffect(() => {
    fetchRows();
    const id = setInterval(fetchRows, intervalMs);
    return () => { clearInterval(id); clearTimeout(retryRef.current); };
  }, [fetchRows, intervalMs]);
  return { rows, loading, error };
};

// Key/value line used inside the expanded detail panels.
const DetailKV = ({ label, value, testid }) => (
  <div className="grid grid-cols-[112px_1fr] gap-3 py-1.5 items-start" data-testid={testid}>
    <span className="text-[11px] uppercase tracking-[0.12em] text-muted-foreground/70 mono pt-0.5">{label}</span>
    <span className="text-[13px] text-foreground/90 mono break-all">{value}</span>
  </div>
);

const BlockDetail = ({ r, amountMode }) => {
  const sizeKb = r.size != null ? `${(r.size / 1024).toFixed(1)} KB` : "—";
  return (
    <div data-testid={`latest-block-detail-${r.height}`} className="px-4 sm:px-6 pt-1 pb-5">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* block details */}
        <div className="rounded-xl border border-primary/15 bg-background/40 grid-bg px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#22d3ee] mono mb-2">block details</div>
          <DetailKV label="Block Hash" testid="bd-hash" value={
            <span className="inline-flex items-center gap-2">{truncMid(r.hash, 14, 9)}<CopyBtn value={r.hash} label="hash" /></span>
          } />
          <DetailKV label="Epoch / Slot" testid="bd-epoch-slot" value={`${r.epoch ?? "—"} / ${r.slot != null ? r.slot.toLocaleString("en-US") : "—"}`} />
          <DetailKV label="Size" testid="bd-size" value={sizeKb} />
          <DetailKV label="Transactions" testid="bd-txs" value={r.tx_count ?? 0} />
          <DetailKV label="Output (ADA)" testid="bd-output" value={formatLovelace(r.output, amountMode)} />
          <DetailKV label="Fees (ADA)" testid="bd-fees" value={formatLovelace(r.fees, amountMode)} />
        </div>
        {/* slot leader */}
        <div className="rounded-xl border border-primary/15 bg-background/40 grid-bg px-4 py-3">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[#22d3ee] mono mb-2">slot leader</div>
          <DetailKV label="Pool" testid="bd-leader" value={
            r.slot_leader ? (
              <span className="inline-flex items-center gap-2">{truncMid(r.slot_leader, 12, 6)}<CopyBtn value={r.slot_leader} label="leader" /></span>
            ) : "—"
          } />
          <DetailKV label="Epoch" testid="bd-leader-epoch" value={r.epoch ?? "—"} />
          <DetailKV label="Slot" testid="bd-leader-slot" value={r.slot != null ? r.slot.toLocaleString("en-US") : "—"} />
          <DetailKV label="Slot Time" testid="bd-leader-time" value={unixToDate(r.time)} />
        </div>
      </div>
    </div>
  );
};

export const LatestBlocks = ({ amountMode, onPickBlock }) => {
  const { rows, loading, error } = useRecentBlocks(20000, 6);
  const [expanded, setExpanded] = useState(null);
  const [, setNow] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setNow((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, []);
  const cols = "grid grid-cols-[2.25rem_0.9fr_1.3fr_0.5fr_1.4fr_1fr_1.1fr_3rem] gap-4 items-center";
  return (
    <div data-testid="latest-blocks" className="relative overflow-hidden border border-primary/25 bg-card/30 backdrop-blur-md rounded-2xl box-glow">
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent opacity-60 tracing-beam pointer-events-none" />
      <div className="px-5 py-3 border-b border-primary/15 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div
            className="w-6 h-6 shrink-0 rounded-md border border-primary/25 bg-primary/[0.06] flex items-center justify-center"
            style={{ filter: "drop-shadow(0 0 4px rgba(157,92,255,0.4))" }}
          >
            <Cube size={12} className="text-primary" weight="bold" />
          </div>
          <span className="text-[11px] uppercase tracking-[0.25em] text-[#22d3ee] mono">latest blocks</span>
          {loading && rows.length === 0 ? <CircleNotch size={11} className="animate-spin text-primary/70" /> : null}
        </div>
        <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground mono">
          {rows.length} most recent · auto-refresh
        </span>
      </div>
      {error && rows.length === 0 ? (
        <div className="px-5 py-6 text-[12px] text-muted-foreground mono">{error.message || "Couldn't load."}</div>
      ) : rows.length === 0 && !loading ? (
        <div className="px-5 py-6 text-[12px] text-muted-foreground mono">No blocks indexed yet.</div>
      ) : (
        <div className="text-[13px] mono tabnum">
          <div className={cn(cols, "hidden md:grid px-5 py-2.5 border-b border-primary/10 text-[10px] uppercase tracking-[0.18em] text-muted-foreground font-sans font-medium")}>
            <span className="w-9" />
            <span>Height</span>
            <span>Time created</span>
            <span className="text-right">Txs</span>
            <span className="text-right">Output</span>
            <span className="text-right">Fees</span>
            <span className="text-right">Slot leader</span>
            <span className="text-right">Actions</span>
          </div>
          <div className="divide-y divide-primary/5">
            {(loading && rows.length === 0
              ? Array.from({ length: 6 }).map((_, i) => ({ skeleton: true, k: i }))
              : rows
            ).map((r, i) =>
              r.skeleton ? (
                <div key={`skel-${r.k}`} className={cn(cols, "px-5 py-3")}>
                  <span className="w-9 h-9 rounded-lg bg-primary/10 animate-pulse" />
                  {[0, 1, 2, 3, 4, 5, 6].map((c) => (
                    <span key={c} className="inline-block h-3 w-16 bg-primary/10 animate-pulse rounded-sm" />
                  ))}
                </div>
              ) : (
                <React.Fragment key={r.hash}>
                <div
                  data-testid={`latest-block-row-${i}`}
                  onClick={() => onPickBlock?.(r.height)}
                  className={cn(
                    "cursor-pointer transition-colors hover:bg-primary/5 px-4 sm:px-5 py-3",
                    "flex flex-wrap items-center gap-x-4 gap-y-2",
                    "md:grid md:gap-4 md:items-center md:grid-cols-[2.25rem_0.9fr_1.3fr_0.5fr_1.4fr_1fr_1.1fr_3rem]",
                    expanded === r.hash && "bg-primary/[0.04]",
                  )}
                >
                  <div className="w-9 h-9 shrink-0 rounded-lg border border-primary/25 bg-primary/[0.06] flex items-center justify-center" style={{ filter: "drop-shadow(0 0 5px rgba(157,92,255,0.4))" }}>
                    <Cube size={16} className="text-primary" weight="bold" />
                  </div>
                  <div className="min-w-0 inline-flex items-center gap-2">
                    <span className="font-heading font-normal text-[15px] text-primary text-glow whitespace-nowrap">
                      #{r.height}
                    </span>
                    <RowShareBtn modeKey="blocks" id={String(r.height)} />
                  </div>
                  <div className="min-w-0 flex-1 md:flex-none">
                    <div className="hidden md:block text-foreground/90 whitespace-nowrap">{unixToDate(r.time)}</div>
                    <div className="text-[11px] md:text-[10px] text-muted-foreground/70 md:text-muted-foreground/60 mono md:mt-0.5 inline-flex items-center gap-1">
                      <Clock size={10} /> {formatRelativeTime(r.time)}
                    </div>
                  </div>
                  <div className="w-full flex flex-wrap gap-x-4 gap-y-1 text-[12px] md:w-auto md:contents">
                    <span className="text-foreground/90 md:text-right md:text-[13px]">
                      <span className="md:hidden text-muted-foreground/60 mr-1 uppercase tracking-wider text-[10px]">Txs</span>{r.tx_count}
                    </span>
                    <span className="hidden md:block text-foreground/90 md:text-right whitespace-nowrap">
                      <span className="md:hidden text-muted-foreground/60 mr-1 uppercase tracking-wider text-[10px]">Output</span>{formatLovelace(r.output, amountMode)}
                    </span>
                    <span className="hidden md:block text-muted-foreground md:text-right whitespace-nowrap">
                      <span className="md:hidden text-muted-foreground/60 mr-1 uppercase tracking-wider text-[10px]">Fees</span>{formatLovelace(r.fees, amountMode)}
                    </span>
                    <span className="hidden md:block text-muted-foreground md:text-right md:truncate">
                      <span className="md:hidden text-muted-foreground/60 mr-1 uppercase tracking-wider text-[10px]">Leader</span>{r.slot_leader ? truncMid(r.slot_leader, 8, 5) : "—"}
                    </span>
                  </div>
                  <div className="ml-auto md:ml-0 flex md:justify-end">
                    <button
                      data-testid={`latest-block-view-${i}`}
                      onClick={(e) => { e.stopPropagation(); setExpanded(expanded === r.hash ? null : r.hash); }}
                      className="inline-flex items-center justify-center h-7 w-7 border border-primary/30 rounded-md text-primary hover:bg-primary/10 transition-colors"
                      aria-label={expanded === r.hash ? "Collapse block details" : "Expand block details"}
                      aria-expanded={expanded === r.hash}
                    >
                      {expanded === r.hash ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
                    </button>
                  </div>
                </div>
                {expanded === r.hash ? <BlockDetail r={r} amountMode={amountMode} /> : null}
                </React.Fragment>
              ),
            )}
          </div>
        </div>
      )}
    </div>
  );
};
